import CryptoJS from 'crypto-js';

// Retrieve or generate a dynamic 256-bit (32 bytes) encryption key for this session
const getSessionKey = (): string => {
  let key = sessionStorage.getItem('_sys_session_enc_key');
  if (!key) {
    // Generate a cryptographically secure random value
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    key = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    sessionStorage.setItem('_sys_session_enc_key', key);
  }
  return key;
};

/**
 * Encrypts a string using standard AES-256 cipher with a dynamic session-specific key.
 * @param text The plain text to encrypt.
 * @returns Base64 encoded string containing the encrypted payload, or an empty string on error.
 */
export const encrypt = (text: string): string => {
  try {
    const key = getSessionKey();
    const encrypted = CryptoJS.AES.encrypt(text, key).toString();
    return btoa(encrypted);
  } catch (e) {
    console.error("SecureStorage encryption error:", e);
    return "";
  }
};

/**
 * Decrypts a string encrypted with the encrypt function.
 * @param encryptedData The base64 encoded payload.
 * @returns The decrypted plain text, or null if decryption fails.
 */
export const decrypt = (encryptedData: string): string | null => {
  try {
    if (!encryptedData) return null;
    const ciphertext = atob(encryptedData);
    const key = getSessionKey();
    const bytes = CryptoJS.AES.decrypt(ciphertext, key);
    const decryptedText = bytes.toString(CryptoJS.enc.Utf8);
    if (!decryptedText) {
      console.warn("SecureStorage: Decryption failed. Possible tampering or key mismatch.");
      return null;
    }
    return decryptedText;
  } catch (e) {
    console.error("SecureStorage decryption error:", e);
    return null;
  }
};
